import { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2 } from 'lucide-react';
import { useFormasPagamentoCombo } from '../hooks/useCadastrosFinanceiros';
import type { CondicaoPagamentoDetalhe, SalvarCondicaoPagamentoRequest, SalvarCondicaoPagamentoParcelaRequest } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SalvarCondicaoPagamentoRequest) => void;
  editingCondicao: CondicaoPagamentoDetalhe | null;
  formaPagRecIdFiltro?: number;
  isLoading?: boolean;
}

const defaultValues: SalvarCondicaoPagamentoRequest = {
  formaPagRecId: 0,
  descricao: '',
  quantidadeParcelas: 1,
  foraMes: false,
  diasFixo: false,
  comissao: false,
  isBloqueado: false,
  parcelas: [],
};

export const CondicaoPagamentoDialog = ({
  open,
  onOpenChange,
  onSubmit,
  editingCondicao,
  formaPagRecIdFiltro,
  isLoading,
}: Props) => {
  const form = useForm<SalvarCondicaoPagamentoRequest>({ defaultValues });
  const { fields, replace, remove } = useFieldArray({ control: form.control, name: 'parcelas' });
  const { formasPagamento } = useFormasPagamentoCombo(true);
  const parcelasWatch = form.watch('parcelas') ?? [];

  useEffect(() => {
    if (!open) return;
    if (editingCondicao) {
      form.reset({
        formaPagRecId: editingCondicao.formaPagRecId,
        codigoCondicao: editingCondicao.codigoCondicao,
        descricao: editingCondicao.descricao ?? '',
        tipoData: editingCondicao.tipoData,
        tipoValor: editingCondicao.tipoValor,
        quantidadeParcelas: editingCondicao.quantidadeParcelas ?? 1,
        diasMes: editingCondicao.diasMes,
        foraMes: editingCondicao.foraMes,
        condicaoVencimento: editingCondicao.condicaoVencimento,
        diasFixo: editingCondicao.diasFixo,
        comissao: editingCondicao.comissao,
        variacao: editingCondicao.variacao,
        perComissao: editingCondicao.perComissao,
        isBloqueado: editingCondicao.isBloqueado,
        parcelas: editingCondicao.parcelas.map(p => ({
          numeroParcela: p.numeroParcela,
          tipoData: p.tipoData,
          diaMes: p.diaMes,
          valorPercentual: p.valorPercentual,
          descontoPercentual: p.descontoPercentual,
          percentualDesconto: p.percentualDesconto,
          descontoValor: p.descontoValor,
          valorDesconto: p.valorDesconto,
        })),
      });
    } else {
      form.reset({ ...defaultValues, formaPagRecId: formaPagRecIdFiltro ?? 0 });
    }
  }, [editingCondicao, open, formaPagRecIdFiltro, form]);

  const gerarParcelas = () => {
    const qtd = form.getValues('quantidadeParcelas') ?? 1;
    const pct = qtd > 0 ? parseFloat((100 / qtd).toFixed(4)) : 100;
    const novas: SalvarCondicaoPagamentoParcelaRequest[] = Array.from({ length: qtd }, (_, i) => ({
      numeroParcela: i + 1,
      valorPercentual: pct,
      descontoPercentual: false,
      descontoValor: false,
    }));
    replace(novas);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingCondicao ? 'Editar Condição de Pagamento' : 'Nova Condição de Pagamento'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Tabs defaultValue="geral">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="geral">Geral</TabsTrigger>
                <TabsTrigger value="vencimento">Vencimento</TabsTrigger>
                <TabsTrigger value="variacao">Variação / Comissão</TabsTrigger>
              </TabsList>

              {/* ── Geral ──────────────────────────────────────────────────── */}
              <TabsContent value="geral" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="formaPagRecId"
                  rules={{ required: 'Forma de pagamento é obrigatória', min: { value: 1, message: 'Selecione uma forma de pagamento' } }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Forma de Pagamento *</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(Number(v))}
                        value={field.value ? field.value.toString() : ''}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {formasPagamento.map(f => (
                            <SelectItem key={f.id} value={f.id.toString()}>{f.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="descricao"
                  rules={{ required: 'Descrição é obrigatória' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: 30/60/90 dias" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="quantidadeParcelas"
                  rules={{ required: 'Informe a quantidade', min: { value: 1, message: 'Mínimo 1' } }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Qtd. Parcelas *</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} {...field} onChange={e => field.onChange(Number(e.target.value))} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isBloqueado"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Switch
                          id="cond-ativo"
                          checked={!(field.value ?? false)}
                          onCheckedChange={(v) => field.onChange(!v)}
                        />
                      </FormControl>
                      <FormLabel htmlFor="cond-ativo" className="!mt-0">Ativo</FormLabel>
                    </FormItem>
                  )}
                />
              </TabsContent>

              {/* ── Vencimento ─────────────────────────────────────────────── */}
              <TabsContent value="vencimento" className="space-y-4 mt-4">
                <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
                  <FormField
                    control={form.control}
                    name="condicaoVencimento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Configuração de Vencimento</FormLabel>
                        <Select
                          onValueChange={v => field.onChange(v === 'none' ? undefined : Number(v))}
                          value={field.value !== undefined && field.value !== null ? field.value.toString() : 'none'}
                        >
                          <FormControl>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">Padrão</SelectItem>
                            <SelectItem value="0">Vencimento original</SelectItem>
                            <SelectItem value="1">Próximo dia útil</SelectItem>
                            <SelectItem value="2">Dia útil anterior</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="diasMes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dias entre Parcelas</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            className="w-36"
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="diasFixo"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 rounded-lg border px-3 py-2">
                        <FormControl>
                          <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="!mt-0 font-normal">Dia fixo para as parcelas</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="foraMes"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 rounded-lg border px-3 py-2">
                        <FormControl>
                          <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="!mt-0 font-normal">Fora do Mês</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              {/* ── Variação / Comissão ────────────────────────────────────── */}
              <TabsContent value="variacao" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="tipoValor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Variação</FormLabel>
                      <Select
                        onValueChange={v => field.onChange(v === 'none' ? undefined : v)}
                        value={field.value ?? 'none'}
                      >
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          <SelectItem value="P">Percentual</SelectItem>
                          <SelectItem value="V">Valor</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="variacao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor da Variação</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          value={field.value ?? ''}
                          onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="comissao"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 rounded-lg border px-3 py-2">
                        <FormControl>
                          <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="!mt-0 font-normal">Gera Comissão</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="perComissao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>% Comissão</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* ── Parcelas ───────────────────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Parcelas</span>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={gerarParcelas}>
                    Gerar Parcelas
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => replace([])}>
                    Limpar
                  </Button>
                </div>
              </div>

              {fields.length > 0 && (
                <div className="border rounded-md overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10 text-center">Nº</TableHead>
                        <TableHead>Tipo Data</TableHead>
                        <TableHead>Dias</TableHead>
                        <TableHead>Valor %</TableHead>
                        <TableHead className="text-center">Desc %</TableHead>
                        <TableHead>% Desc</TableHead>
                        <TableHead className="text-center">Desc Val</TableHead>
                        <TableHead>Val Desc</TableHead>
                        <TableHead className="w-8" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fields.map((field, index) => (
                        <TableRow key={field.id}>
                          <TableCell className="text-center font-medium text-sm">{index + 1}</TableCell>

                          <TableCell>
                            <Input
                              className="h-7 w-24 text-xs"
                              placeholder="Tipo"
                              {...form.register(`parcelas.${index}.tipoData`)}
                            />
                          </TableCell>

                          <TableCell>
                            <Input
                              className="h-7 w-16 text-xs"
                              type="number"
                              {...form.register(`parcelas.${index}.diaMes`, { valueAsNumber: true })}
                            />
                          </TableCell>

                          <TableCell>
                            <Input
                              className="h-7 w-20 text-xs"
                              type="number"
                              step="0.0001"
                              {...form.register(`parcelas.${index}.valorPercentual`, { valueAsNumber: true })}
                            />
                          </TableCell>

                          <TableCell className="text-center">
                            <Controller
                              control={form.control}
                              name={`parcelas.${index}.descontoPercentual`}
                              render={({ field: f }) => (
                                <Switch
                                  checked={f.value ?? false}
                                  onCheckedChange={f.onChange}
                                  className="scale-75"
                                />
                              )}
                            />
                          </TableCell>

                          <TableCell>
                            <Input
                              className="h-7 w-20 text-xs"
                              type="number"
                              step="0.01"
                              disabled={!(parcelasWatch[index]?.descontoPercentual)}
                              {...form.register(`parcelas.${index}.percentualDesconto`, { valueAsNumber: true })}
                            />
                          </TableCell>

                          <TableCell className="text-center">
                            <Controller
                              control={form.control}
                              name={`parcelas.${index}.descontoValor`}
                              render={({ field: f }) => (
                                <Switch
                                  checked={f.value ?? false}
                                  onCheckedChange={f.onChange}
                                  className="scale-75"
                                />
                              )}
                            />
                          </TableCell>

                          <TableCell>
                            <Input
                              className="h-7 w-20 text-xs"
                              type="number"
                              step="0.01"
                              disabled={!(parcelasWatch[index]?.descontoValor)}
                              {...form.register(`parcelas.${index}.valorDesconto`, { valueAsNumber: true })}
                            />
                          </TableCell>

                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => remove(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : editingCondicao ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
